import { toaster } from "@/components/ui/toaster";
import { RequestTypes, UnidadCotizacionServicio } from "@/constants/enums";
import { AppRoutes } from "@/constants/routes";
import {
  CatalogoServicioItem,
  createSolicitudServicio,
  getSolicitudesServicio,
} from "@/services/service-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { parseTonInputToNumber } from "@/utils/query-string";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { SamplingRequestsListResponse } from "./sampling-requests.schema";

interface CreateSamplingRequestHandlerProps {
  values: any;
  setLoadingCreate: (loading: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  catalogo: CatalogoServicioItem[];
}

export const createSamplingRequestHandler = async ({
  values,
  setLoadingCreate,
  navigate,
  fetchPermissions,
  catalogo,
}: CreateSamplingRequestHandlerProps) => {
  const normalizeSingle = (v: any) => (Array.isArray(v) ? v?.[0] : v);

  const cuenta = Number(normalizeSingle(values?.account?.value));
  if (!Number.isFinite(cuenta) || cuenta <= 0) return;

  const usuarioMobileRaw = values?.usuarioMobile?.value;
  const usuarioMobile = usuarioMobileRaw ? Number(usuarioMobileRaw) : undefined;

  const tipoServicioRaw = normalizeSingle(values?.tipoServicio);
  const tipoServicio = Number(tipoServicioRaw);
  if (!Number.isFinite(tipoServicio) || tipoServicio <= 0) return;

  const selectedServicio = catalogo.find((c) => Number(c.id) === tipoServicio);
  const unidadCotizacion = selectedServicio?.unidadCotizacion;
  const valorUnitario = Number(selectedServicio?.valorUnitario ?? 0);

  const idProcedenciaRaw = String(normalizeSingle(values?.procedencia) ?? "");
  if (!idProcedenciaRaw) return;

  const idProcedencia = idProcedenciaRaw;
  const fechaDeseada = dayjs(values?.fechaDeseada).toISOString();

  const hectareas = parseTonInputToNumber(values?.hectareas) ?? 0;
  const cantidadMuestras =
    Math.trunc(Number(values?.cantidadMuestras ?? 0)) || 0;

  const valorTotalBase =
    unidadCotizacion === UnidadCotizacionServicio.CantidadMuestra
      ? cantidadMuestras
      : hectareas;

  const valorTotal = Number.isFinite(valorUnitario)
    ? Number((valorUnitario * (valorTotalBase ?? 0)).toFixed(2))
    : 0;

  const observaciones = String(values?.observaciones ?? "")
    .trim()
    .slice(0, 50);

  setLoadingCreate(true);
  try {
    await createSolicitudServicio({
      cuentaOrigen: cuenta,
      ...(usuarioMobile ? { usuarioMobile } : {}),
      tipoServicio,
      idProcedencia,
      fechaDeseada,
      hectareas,
      cantidadMuestras,
      valorTotal,
      observaciones,
      tipoSolicitud: RequestTypes.Muestreo,
    });

    toaster.create({
      type: "success",
      title: "Solicitud creada exitosamente.",
    });

    navigate(AppRoutes.SAMPLING_REQUESTS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SAMPLING_REQUESTS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error, "No se pudo crear la solicitud."),
      });
    }

    throw error;
  } finally {
    setLoadingCreate(false);
  }
};

interface FetchSamplingRequestsHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<SamplingRequestsListResponse | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchSamplingRequestsHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchSamplingRequestsHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getSolicitudesServicio({
      CuentaOrigen: filters?.cuentaOrigen?.value
        ? Number(filters.cuentaOrigen.value)
        : undefined,
      TipoServicio: filters?.tipoServicio?.[0]
        ? Number(filters.tipoServicio[0])
        : undefined,
      TipoSolicitud: RequestTypes.Muestreo,
      FechaDesde: filters?.FechaDesde,
      FechaHasta: filters?.FechaHasta,
      Estados: filters?.Estados,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    });

    setList(response ?? undefined);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudieron obtener las solicitudes de muestreo.",
        ),
      });
    }

    throw error;
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitSamplingFiltersHandlerProps {
  event: FieldValues;
  setPage: Dispatch<SetStateAction<number>>;
  fetchList: (filters?: any) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  filtersEmpty: boolean;
}

export const submitSamplingFiltersHandler = async ({
  event,
  setPage,
  fetchList,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  filtersEmpty,
}: SubmitSamplingFiltersHandlerProps) => {
  const estados = Array.isArray(event?.estados)
    ? event.estados.map(Number).filter((v) => !Number.isNaN(v))
    : undefined;

  const tipoServicio = Array.isArray(event?.tipoServicio)
    ? event.tipoServicio
    : undefined;

  console.log(event);

  const nextFilters = {
    cuentaOrigen: event?.cuentaOrigen || undefined,
    tipoServicio: tipoServicio?.length ? tipoServicio : undefined,
    Estados: estados?.length ? estados : undefined,
    FechaDesde: event?.fechaDesde,
    FechaHasta: event?.fechaHasta,
    "P.Pagina": 1,
  };

  setPage(1);
  setCurrentFilters(nextFilters);
  setFiltersApplied(!filtersEmpty);
  setOpenFilters(false);

  await fetchList(nextFilters);
};

interface ClearSamplingFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  fetchList: (filters?: any) => Promise<void>;
}

export const clearSamplingFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  setCurrentFilters,
  fetchList,
}: ClearSamplingFiltersHandlerProps) => {
  setValue("cuentaOrigen", undefined);
  setValue("tipoServicio", []);
  setValue("estados", []);
  setValue("fechaDesde", undefined);
  setValue("fechaHasta", undefined);

  setFiltersApplied(false);
  setOpenFilters(false);
  setPage(1);
  setCurrentFilters(undefined);

  await fetchList({ "P.Pagina": 1 });
};
